import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ActionButton, Buttons, CancelButton, Drawer, Table } from '~/components';
import { useApi, useToast, useWorkspace } from '~/contexts';
import { useDirtyCheck, useForm, useIsMounted } from '~/hooks';
import { PageLoader } from '~/routes/public/pages';
import OptionSelect from './OptionSelect';

export default function TaxRateMappings({ onClose }) {
  const { workspace } = useWorkspace();
  const api = useApi();
  const toast = useToast();
  const [{ isSubmitting, saved }, form] = useForm();
  const isMounted = useIsMounted();
  const [dirty, setDirty] = useState(false);
  const dirtyCheck = useDirtyCheck(() => dirty);
  const { sageConnectionId } = useParams();

  const [{ taxRates, taxDetails, isReady }, setState] = useState({ taxRates: null, isReady: false });

  useEffect(() => {
    (async () => {
      const { data: taxRates } = await api.www.workspaces(workspace.id).sage(sageConnectionId).getTaxRateMappings();
      const { data: taxDetails } = await api.www.workspaces(workspace.id).sage(sageConnectionId).getTaxDetails();
      if (!isMounted.current) return;
      setState({ taxRates, taxDetails, isReady: true });
    })();
  }, [api, workspace.id, isMounted, sageConnectionId]);

  const updateMapping = ({ taxRateId, sageTaxDetailId }) => {
    setDirty(true);
    setState((state) => ({
      ...state,
      taxRates: state.taxRates.map((t) => (t.id === taxRateId ? { ...t, sageTaxDetailId } : t)),
    }));
  };

  return (
    <Drawer
      isOpen
      title="Tax Rate Mappings"
      width="70rem"
      onBeforeClose={({ setIsOpen }) => dirtyCheck(() => setIsOpen(false))}
      onClose={onClose}>
      {(closeDrawer) => {
        const handleCloseClick = () => dirtyCheck(() => closeDrawer());

        const handleSave = async () => {
          form.submit();
          try {
            const mappings = taxRates.map((t) => ({ taxRateId: t.id, sageTaxDetailId: t.sageTaxDetailId }));
            await api.www.workspaces(workspace.id).sage(sageConnectionId).updateTaxRateMappings(mappings);
            form.save();
            closeDrawer();
          } catch (error) {
            toast.error(error.message);
            form.done();
          }
        };

        if (!isReady) return <PageLoader />;

        return (
          <>
            <Table style={{ marginBottom: '2rem' }}>
              <Table.BoxHeader>
                <Table.Column width="40%">Tax Rate</Table.Column>
                <Table.Column>Sage Tax Detail</Table.Column>
              </Table.BoxHeader>
              <Table.Body>
                {taxRates.map((taxRate) => (
                  <Table.BoxRow key={taxRate.id}>
                    <Table.Cell>{taxRate.name}</Table.Cell>
                    <Table.Cell>
                      <OptionSelect
                        defaultOptions={taxDetails}
                        typeLabel="Tax Detail"
                        value={taxRate.sageTaxDetailId}
                        onChange={(event) =>
                          updateMapping({ taxRateId: taxRate.id, sageTaxDetailId: event.target.value || null })
                        }
                      />
                    </Table.Cell>
                  </Table.BoxRow>
                ))}
              </Table.Body>
            </Table>
            <Drawer.Actions>
              <Buttons align="right">
                <CancelButton onClick={handleCloseClick}>Close</CancelButton>
                <ActionButton isLoading={isSubmitting} ok={saved} onClick={handleSave}>
                  Save &amp; Close
                </ActionButton>
              </Buttons>
            </Drawer.Actions>
          </>
        );
      }}
    </Drawer>
  );
}
